import { Map as ImmutableMap } from 'immutable';
import { v4 as uuid } from 'uuid';

import { getCategoriesById } from 'app-components/flux/categories/categoriesSelectors';
import { getPayeesForTransactions } from 'app-components/flux/payees/payeesSelectors';
import { createDialog } from 'app-components/flux/root-ui/rootUiActions';
import { mkRootUiData } from 'app-components/flux/root-ui/rootUiTypes';
import { spendingWatchlistSelectors, type Watchlist } from 'app-components/flux/spending-watchlist';
import { getTags } from 'app-components/flux/tags/tagsSelectors';
import { getLogger } from 'app-components/utils/core';
import store from 'app-components/utils/redux-store';
import type RootState from 'app-components/utils/redux-store/rootState';

import { safeParse } from '../../aiChatUtils';
import { returnToolResponse } from '../llmToolsFramework';
import type { ToolMessage } from '../llmToolsTypes';

const log = getLogger('llmWatchlistTools');

type FilterType = 'categories' | 'payees' | 'tags';

const normalizeName = (value?: string) => value?.trim().toLowerCase();

const resolveFilterItems = (
  state: RootState,
  filterType: FilterType | undefined,
  filterItems: string[] | undefined,
) => {
  if (!filterType || !filterItems?.length) return undefined;

  if (filterType === 'categories') {
    const categories = getCategoriesById(state);
    const result: Array<{ id: string; type: 'CATEGORY'; name: string }> = [];
    for (const name of filterItems) {
      const normalizedName = name.toLowerCase().trim();
      const found = categories.find((cat) => cat.name?.toLowerCase().trim() === normalizedName);
      if (found?.id && found?.name) {
        result.push({ id: found.id, type: 'CATEGORY', name: found.name });
      } else {
        log.warn('resolveFilterItems: Category not found', { name });
      }
    }
    return result;
  }

  if (filterType === 'payees') {
    const payeesByName = getPayeesForTransactions(state);
    const result: string[] = [];
    for (const name of filterItems) {
      const normalizedName = name.toLowerCase().trim();
      const found = payeesByName.get(normalizedName);
      if (found) {
        result.push(found);
      } else {
        log.warn('resolveFilterItems: Payee not found', { name });
      }
    }
    return result;
  }

  if (filterType === 'tags') {
    const tags = getTags(state);
    const result: Array<{ id: string; name: string }> = [];
    for (const name of filterItems) {
      const normalizedName = name.toLowerCase().trim();
      const found = tags.find((tag) => tag.name?.toLowerCase().trim() === normalizedName);
      if (found?.id && found?.name) {
        result.push({ id: found.id, name: found.name });
      } else {
        log.warn('resolveFilterItems: Tag not found', { name });
      }
    }
    return result;
  }

  return undefined;
};

const formatWatchlistForAI = (watchlist: Watchlist) => ({
  id: watchlist.id,
  name: watchlist.name,
  emoji: watchlist.emoji,
  targetAmount: watchlist.targetAmount,
  monthToDate: watchlist.thisMonth,
  yearToDate: watchlist.yearToDate,
  period: watchlist.period,
  startDate: watchlist.startDate,
  endDate: watchlist.endDate,
});

const getWatchlistsForAI = () => {
  const state = store?.getState() as RootState;
  const watchlists = spendingWatchlistSelectors.getSortedWatchlists(state);
  return watchlists.toArray().map(formatWatchlistForAI);
};

const findExistingWatchlistForUpsert = (state: RootState, watchlistId?: string, name?: string) => {
  if (watchlistId) {
    const byId = spendingWatchlistSelectors.getSpendingWatchListById(state, watchlistId);
    if (byId) return byId;
  }

  const normalizedName = normalizeName(name);
  if (!normalizedName) return undefined;

  const watchlists = spendingWatchlistSelectors.getSortedWatchlists(state);
  return watchlists.find((watchlist: Watchlist) => normalizeName(watchlist.name) === normalizedName);
};

export const watchlistTools = {
  spending_watchlist_list: {
    definition: {
      name: 'spending_watchlist_list',
      description:
        'Get the list of spending watchlists. A spending watchlist tracks spending for specific categories, payees, or tags over time. Returns spending watchlist name, spending amounts (month-to-date, year-to-date), target amount if set, and emoji. ' +
        'Politely decline if a user asks to use this tool for investments. Be concise and brief in your response.',
      parameters: {
        type: 'object' as const,
        properties: {},
        required: [],
      },
    },
    handler: (msg: ToolMessage) => {
      const watchlists = getWatchlistsForAI();
      log.debug('spending_watchlist_list: Returning watchlists', { count: watchlists.length });

      if (watchlists.length === 0) {
        return returnToolResponse(msg, {
          watchlists: [],
          message: 'No spending watchlists found. Would you like to create one?',
        });
      }

      return returnToolResponse(msg, {
        watchlists,
        count: watchlists.length,
      });
    },
  },

  spending_watchlist_get: {
    definition: {
      name: 'spending_watchlist_get',
      description:
        'Get details of a specific spending watchlist by name or ID. ' +
        'Politely decline if a user asks to use this tool for investments. Be concise and brief in your response.',
      parameters: {
        type: 'object' as const,
        properties: {
          nameOrId: {
            type: 'string' as const,
            description: 'The spending watchlist name or ID to look up',
          },
        },
        required: ['nameOrId'],
      },
    },
    handler: (msg: ToolMessage) => {
      const args = safeParse(msg.arguments) || {};
      const { nameOrId } = args;

      const state = store?.getState() as RootState;
      const watchlists = spendingWatchlistSelectors.getSortedWatchlists(state);

      const normalizedSearch = nameOrId?.toLowerCase().trim();
      const found = watchlists.find(
        (w: Watchlist) => w.id === nameOrId || w.name?.toLowerCase().trim() === normalizedSearch,
      );

      if (!found) {
        return returnToolResponse(msg, {
          success: false,
          error: `Spending watchlist "${nameOrId}" not found.`,
        });
      }

      return returnToolResponse(msg, {
        success: true,
        watchlist: formatWatchlistForAI(found),
      });
    },
  },

  upsert_spending_watchlist: {
    definition: {
      name: 'upsert_spending_watchlist',
      description: `Create or update a spending watchlist. Opens a dialog for the user to review and confirm. All fields are optional.
Politely decline if a user asks to use this tool for investments.

If watchlistId is provided, updates that spending watchlist. Otherwise creates a new one.
Use watchlist_list or watchlist_get first to find an existing spending watchlist if the user wants to modify one.

- watchlistId: ID of existing spending watchlist to update (from watchlist_list)
- name: Spending watchlist name (e.g., "Coffee Spending")
- filterType: What to track - "categories", "payees", or "tags" (only for create)
- filterItems: List of items to track (only for create)
- targetAmount: Monthly spending target/budget
- emoji: Emoji for the spending watchlist

Be concise and brief in your response.`,
      parameters: {
        type: 'object' as const,
        properties: {
          watchlistId: {
            type: 'string' as const,
            description: 'ID of existing spending watchlist to update (omit to create new)',
          },
          name: {
            type: 'string' as const,
            description: 'Spending watchlist name',
          },
          filterType: {
            type: 'string' as const,
            enum: ['categories', 'payees', 'tags'],
            description: 'Type of filter (only for create)',
          },
          filterItems: {
            type: 'array' as const,
            items: { type: 'string' as const },
            description: 'List of items to track (only for create)',
          },
          targetAmount: {
            type: 'number' as const,
            description: 'Monthly spending target/budget',
          },
          emoji: {
            type: 'string' as const,
            description: 'Emoji for the spending watchlist',
          },
        },
        required: [],
      },
    },
    handler: (msg: ToolMessage) => {
      const args = safeParse(msg.arguments) || {};
      const { watchlistId, name, filterType, filterItems, targetAmount, emoji } = args;

      const state = store?.getState() as RootState;
      const existingWatchlist = findExistingWatchlistForUpsert(state, watchlistId, name);
      const operation = existingWatchlist ? 'update' : 'create';
      const resolvedWatchlistId = existingWatchlist?.id ?? watchlistId;

      const resolvedFilterItems = resolveFilterItems(state, filterType, filterItems);

      log.debug('upsert_spending_watchlist:', {
        operation,
        watchlistId,
        resolvedWatchlistId,
        filterType,
        resolvedFilterItems,
      });

      return new Promise((resolve) => {
        const callback = (result: { success: boolean; watchlistId?: string; error?: string }) => {
          if (result.success) {
            log.debug('upsert_spending_watchlist: Success', { operation });
            resolve(
              returnToolResponse(msg, { success: true, message: `Spending watchlist ${operation}d successfully.` }),
            );
          } else {
            log.error('upsert_spending_watchlist: Failed', { error: result.error });
            resolve(
              returnToolResponse(msg, {
                success: false,
                error: result.error || `Failed to ${operation} spending watchlist`,
              }),
            );
          }
        };

        const data = {
          watchlistId: resolvedWatchlistId,
          name,
          filterType,
          filterItems: resolvedFilterItems,
          targetAmount,
          emoji,
        };

        const dialogData = mkRootUiData({
          type: 'LLM_WATCHLIST',
          id: `llm-watchlist-${operation}-${uuid()}`,
          visible: true,
          allowNesting: false,
          props: ImmutableMap({ operation, data, callback }),
        });

        log.debug('upsert_spending_watchlist: Dispatching dialog');
        store?.dispatch(createDialog(dialogData));
      });
    },
  },
};
